/**
 * ResourceDigestStatus
 * ----
 * Surfaces the outcome of a resource's best-effort AI digest to the lecturer:
 * whether the AI was able to read the resource (and the digest it built), whether
 * it is still processing, or whether it failed (with a human reason and a retry).
 *
 * The digest is the AI's only knowledge of a resource's content - it feeds grading,
 * rubric and quiz generation - so a silent failure means the resource is effectively
 * invisible to the AI. This makes that state explicit and recoverable.
 */
import React, { useEffect, useState } from 'react';
import {
  VStack, HStack, Text, Box, Icon, Pressable, Button, ButtonText, ScrollView, Switch,
} from '@gluestack-ui/themed';
import {
  CircleCheck, TriangleAlert, RefreshCw, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { Resource } from '@/common/api/models/resource';
import MarkdownText from '@/common/components/MarkdownText';

/** Media types that go through the digest pipeline on the backend. */
const DIGESTIBLE_TYPES = ['link', 'document', 'video'];

/**
 * Failure reasons that are permanent — retrying will produce the same outcome unless
 * the resource URL or content changes. The Retry button is hidden for these so
 * lecturers are not misled into clicking repeatedly.
 * Transient reasons (timeout, unreachable, error) keep the Retry button.
 */
const PERMANENT_FAILURE_REASONS = new Set(['blocked', 'not_found', 'no_content', 'unsupported_content']);

/** Same 11-character video id forms the backend accepts for Gemini film reads. */
const YOUTUBE_FILM_VIDEO_ID_PATTERNS = [
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?[^#]*v=([a-zA-Z0-9_-]{11})/,
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  /(?:https?:\/\/)?(?:www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
  /(?:https?:\/\/)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
];

const extractYoutubeFilmVideoId = (url: string): string | null => {
  for (const pattern of YOUTUBE_FILM_VIDEO_ID_PATTERNS) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }
  return null;
};

const isYoutubeFilmUrl = (url?: string): boolean => {
  if (!url) {
    return false;
  }
  const videoId = extractYoutubeFilmVideoId(url);
  return !!videoId && videoId.length === 11;
};

const DIGEST_PANEL_MAX_HEIGHT = 260;

interface ResourceDigestStatusProps {
  resource: Resource & { reprocessing?: boolean };
  onRetry: () => void;
  isRetrying: boolean;
  readonly: boolean;
  /**
   * Epoch ms until which we have positive evidence a digest job is in flight for
   * this resource (it was just created, or a retry was just triggered). Absence of
   * digest metadata past this window is NOT treated as "still processing" - there is
   * no durable server-side job status to poll, so a resource can silently end up with
   * no digest and no job running (see docs/lessons-learned.md 2026-07 digest gap).
   */
  digestPendingUntil?: number;
  /** When false, hide the oral-exam relevance switch (e.g. oral_exam / event tasks). */
  showExamRelevant?: boolean;
  onOralExamRelevantChange?: (value: boolean) => void;
  isOralExamRelevantUpdating?: boolean;
}

/** How often we re-check whether the pending window has expired, to flip the UI without a reload. */
const PENDING_CHECK_INTERVAL_MS = 2000;

const ResourceDigestStatus: React.FC<ResourceDigestStatusProps> = ({
  resource,
  onRetry,
  isRetrying,
  readonly,
  digestPendingUntil,
  showExamRelevant = false,
  onOralExamRelevantChange,
  isOralExamRelevantUpdating = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const isWithinPendingWindow = !!digestPendingUntil && now < digestPendingUntil;

  useEffect(() => {
    if (!digestPendingUntil || now >= digestPendingUntil) {
      return undefined;
    }
    const interval = setInterval(() => setNow(Date.now()), PENDING_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [digestPendingUntil, now]);

  if (!DIGESTIBLE_TYPES.includes(resource.type)) {
    return null;
  }

  const digest = (resource.metadata || []).find((m) => m.type === 'digest');
  const content = (digest?.content || {}) as {
    status?: string;
    markdown?: string;
    reason?: string;
  };
  const markdown = typeof content.markdown === 'string' ? content.markdown : '';

  // Still working: a client-side retry/creation just fired and is within its grace
  // window. Once that window lapses without a digest landing, we no longer assume a
  // job is running - see the "not generated" state below.
  const isObsoleteYoutubeDigest = isYoutubeFilmUrl(resource.url)
    && content.status === 'failed'
    && content.reason === 'obsolete';
  const isProcessing = !!resource.reprocessing || isRetrying || (!digest && isWithinPendingWindow)
    || isObsoleteYoutubeDigest;

  if (isProcessing) {
    return (
      <Box
        mt="$3"
        bg="$bgSubtle"
        borderWidth={1}
        borderColor="$borderLight"
        borderRadius="$sm"
        px="$3"
        py="$2"
        testID="digest-status-processing"
      >
        <HStack space="sm" alignItems="center">
          <Icon as={RefreshCw} size="xs" color="$textHint" />
          <Text size="xs" color="$textSubtle">
            {isRetrying ? t('lecturer.resourceDigest.retrying') : t('lecturer.resourceDigest.processing')}
          </Text>
        </HStack>
        {!readonly && isObsoleteYoutubeDigest && (
          <Button
            size="xs"
            variant="outline"
            action="secondary"
            alignSelf="flex-start"
            onPress={onRetry}
            mt="$2"
            testID="digest-retry-button"
          >
            <Icon as={RefreshCw} size="2xs" mr="$1" color="$textHeading" />
            <ButtonText>{t('lecturer.resourceDigest.retry')}</ButtonText>
          </Button>
        )}
      </Box>
    );
  }

  // No digest metadata at all, and no evidence a job is currently running: don't
  // pretend we're "still working" - let the lecturer trigger it explicitly instead.
  if (!digest) {
    return (
      <Box
        bg="$bgSubtle"
        borderWidth={1}
        borderColor="$borderLight"
        borderRadius="$sm"
        p="$2"
        mt="$3"
        testID="digest-status-not-generated"
      >
        <HStack space="sm" alignItems="flex-start">
          <Icon as={Sparkles} size="xs" color="$textSubtle" mt="$1" />
          <VStack flex={1} space="xs">
            <Text size="2xs" color="$textHeading" fontWeight="medium">
              {t('lecturer.resourceDigest.notGenerated')}
            </Text>
            <Text size="2xs" color="$textHint">
              {t('lecturer.resourceDigest.notGeneratedHint')}
            </Text>
            {!readonly && (
              <Button
                size="xs"
                variant="outline"
                action="secondary"
                alignSelf="flex-start"
                onPress={onRetry}
                mt="$1"
                testID="digest-generate-button"
              >
                <Icon as={Sparkles} size="2xs" mr="$1" color="$textHeading" />
                <ButtonText>{t('lecturer.resourceDigest.generate')}</ButtonText>
              </Button>
            )}
          </VStack>
        </HStack>
      </Box>
    );
  }

  const digestIsUsable = !!markdown && content.status !== 'failed'
    && (content.status === 'ok' || !content.status);

  // Success: a usable digest exists (status 'ok' or a legacy digest with markdown).
  if (digestIsUsable) {
    const toggleLabel = expanded
      ? t('lecturer.resourceDigest.hideDigest')
      : t('lecturer.resourceDigest.viewDigest');
    const examRelevantValue = resource.oralExamRelevant !== false;
    const showExamRelevantRow = showExamRelevant && !!onOralExamRelevantChange;

    return (
      <VStack mt="$3" testID="digest-status-ready">
        <Box
          bg="$bgSubtle"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$sm"
          overflow="hidden"
        >
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            testID="digest-view-toggle"
            accessibilityRole="button"
            accessibilityState={{ expanded }}
          >
            <HStack
              alignItems="center"
              justifyContent="space-between"
              px="$3"
              py="$2"
              sx={{
                _web: {
                  cursor: 'pointer',
                  ':hover': { bg: '$bgHover' },
                },
              }}
            >
              <HStack space="sm" alignItems="center" flex={1}>
                <Icon as={CircleCheck} size="xs" color="$statusSuccess" />
                <Text size="xs" color="$textHeading" fontWeight="medium">
                  {t('lecturer.resourceDigest.ready')}
                </Text>
              </HStack>
              <HStack space="xs" alignItems="center">
                <Text size="2xs" color="$textBrand">{toggleLabel}</Text>
                <Icon as={expanded ? ChevronUp : ChevronDown} size="xs" color="$textBrand" />
              </HStack>
            </HStack>
          </Pressable>
          {!!showExamRelevantRow && (
            <HStack
              space="sm"
              alignItems="center"
              px="$3"
              py="$2"
              borderTopWidth={1}
              borderTopColor="$borderLight"
            >
              <Switch
                size="sm"
                testID={`resource-oral-exam-relevant-${resource.id}`}
                value={examRelevantValue}
                onValueChange={onOralExamRelevantChange}
                isDisabled={readonly || isOralExamRelevantUpdating}
              />
              <Text size="xs" color="$textBody">
                {t('lecturer.resourceDigest.examRelevant')}
              </Text>
            </HStack>
          )}
          {!!expanded && (
            <Box
              bg="$bgCard"
              borderTopWidth={1}
              borderTopColor="$borderLight"
              px="$3"
              py="$2"
              maxHeight={DIGEST_PANEL_MAX_HEIGHT}
              testID="digest-markdown"
            >
              <ScrollView
                showsVerticalScrollIndicator
                nestedScrollEnabled
              >
                <MarkdownText content={markdown} />
              </ScrollView>
            </Box>
          )}
        </Box>
      </VStack>
    );
  }

  // Failure: explain why, and offer a retry only for transient failures.
  const reasonKey = content.reason || 'error';
  const isPermanentFailure = PERMANENT_FAILURE_REASONS.has(reasonKey);
  const useFilmReasonCopy = isYoutubeFilmUrl(resource.url) && ['blocked', 'not_found', 'no_content'].includes(reasonKey);
  const reasonTranslationKey = useFilmReasonCopy
    ? `lecturer.resourceDigest.film.${reasonKey}`
    : `lecturer.resourceDigest.reason.${reasonKey}`;
  return (
    <Box
      bg="$bgBrandSubtle"
      borderLeftWidth={3}
      borderLeftColor="$statusWarning"
      borderRadius="$sm"
      p="$2"
      mt="$3"
      testID="digest-status-failed"
    >
      <HStack space="sm" alignItems="flex-start">
        <Icon as={TriangleAlert} size="xs" color="$statusWarning" mt="$1" />
        <VStack flex={1} space="xs">
          <Text size="2xs" color="$textHeading" fontWeight="medium">
            {t('lecturer.resourceDigest.failedTitle')}
          </Text>
          <Text size="2xs" color="$textSubtle">
            {t(reasonTranslationKey)}
          </Text>
          {useFilmReasonCopy ? null : (
            <Text size="2xs" color="$textHint">
              {isPermanentFailure
                ? t(`lecturer.resourceDigest.permanentHint.${reasonKey}`)
                : t('lecturer.resourceDigest.failedHint')}
            </Text>
          )}
          {!readonly && !isPermanentFailure && (
            <Button
              size="xs"
              variant="outline"
              action="secondary"
              alignSelf="flex-start"
              onPress={onRetry}
              mt="$1"
              testID="digest-retry-button"
            >
              <Icon as={RefreshCw} size="2xs" mr="$1" color="$textHeading" />
              <ButtonText>{t('lecturer.resourceDigest.retry')}</ButtonText>
            </Button>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export default ResourceDigestStatus;
